import { Link } from '@tanstack/react-router';
import { ReceiptText, Ticket, Trophy, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type LotteryRailView = 'game' | 'tickets' | 'results';

interface RailItem {
  key: LotteryRailView;
  icon: LucideIcon;
  label: string;
  to: string;
}

/**
 * Our own nav for the frameless SUPERM lottery shell — vertical rail right of
 * the iframe on md+, horizontal bottom bar on mobile. The active item is
 * inert; the bet item leads back to the lottery list (pick a game) when a
 * deep view (tickets/results) is open.
 */
export function LotterySideRail({ active }: { active: LotteryRailView }) {
  const { t } = useTranslation(['casino', 'common']);

  const items: RailItem[] = [
    { key: 'game', icon: Ticket, label: t('common:lottery.bet'), to: '/lottery' },
    { key: 'tickets', icon: ReceiptText, label: t('casino:lottery.myTickets'), to: '/lottery/tickets' },
    { key: 'results', icon: Trophy, label: t('casino:lottery.results'), to: '/lottery/results' },
  ];

  const itemCls = (isActive: boolean) =>
    [
      'flex flex-col items-center justify-center gap-0.5 rounded-xl transition-colors',
      'h-12 flex-1 md:h-12 md:w-12 md:flex-none',
      isActive
        ? 'bg-[var(--accent)] text-[var(--accent-on)] shadow-[0_4px_14px_var(--accent-glow)]'
        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]',
    ].join(' ');

  return (
    <nav
      aria-label={t('casino:tabs.lottery')}
      className="flex shrink-0 flex-row items-stretch gap-1 border-t border-[var(--border)] bg-[var(--bg-raised)] px-2 py-1.5 md:w-16 md:flex-col md:items-center md:border-l md:border-t-0 md:px-2 md:py-3"
    >
      {items.map(({ key, icon: IconCmp, label, to }) => {
        const isActive = key === active;
        const body = (
          <>
            <IconCmp size={20} />
            <span className="text-[10px] font-bold leading-none md:hidden">{label}</span>
          </>
        );
        if (isActive) {
          return (
            <span key={key} title={label} aria-current="page" className={itemCls(true)}>
              {body}
            </span>
          );
        }
        return (
          <Link key={key} to={to} title={label} className={itemCls(false)}>
            {body}
          </Link>
        );
      })}
    </nav>
  );
}
